/**
 * @fileoverview Input/output schemas for world-object-snapshotter-v3.
 *
 * This agent updates a world object's state after a chapter has been written.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { WorldObjectAttributesV3Schema, WorldObjectSnapshotV3Schema } from '@/domains/world-building/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  // The world object to update
  worldObject: z.object({
    id: z.string(),
    name: z.string(),
    type: z.literal('object'),
    element: z.enum(['artifact', 'item']),
    initialDescription: z.string().optional(),
  }).describe('The world object being snapshotted.'),
  // The previous snapshot (state before this chapter)
  previousSnapshot: WorldObjectSnapshotV3Schema.optional().describe('The object state before this chapter.'),
  // The chapter content to analyze
  chapterContent: z.string().describe('The full content of the chapter to analyze.'),
  chapterNumber: z.number().describe('The chapter number for snapshot metadata.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output matches the world object attributes schema.
 * The hook will wrap this in snapshot metadata (id, chapterId, etc.).
 */
export const OutputSchema = WorldObjectAttributesV3Schema.extend({
  notPresentInChapter: z.boolean().optional().describe('True if the object was not mentioned or relevant in this chapter.'),
});

export type Output = z.infer<typeof OutputSchema>;
